import { z } from 'zod'

// Define the enum values based on the GraphQL schema
const SurveyTypeEnum = z.enum([
  'CustomerEffortScore',
  'CustomerSatisfactionScore',
  'NetPromoterScore',
  'Onboarding',
  'OpenFeedback',
  'ProductMarketFit',
  'UserInterview',
])

const FnsQuestionTypeEnum = z.enum([
  'CTA',
  'MultipleChoice',
  'Rating',
  'SingleChoice',
  'Text',
  'YesNo',
])

const FnsUiTemplateEnum = z.enum([
  'CTA',
  'Default',
  'Emoji',
  'Faces',
  'Scale',
  'Stars',
])

// Schema for individual question options
const fnsQuestionOptionSchema = z.string().min(1, 'Option text is required')

// Schema for individual question
const fnsQuestionSchema = z
  .object({
    questionId: z.string().optional(), // For existing questions when updating
    index: z.number().min(1),
    text: z.string().min(1, 'Question text is required'),
    type: FnsQuestionTypeEnum,
    isRequired: z.boolean(),
    isActive: z.boolean().optional(),
    uiTemplate: FnsUiTemplateEnum.optional(),
    options: z.array(fnsQuestionOptionSchema).optional(),
    minValue: z.number().optional(),
    maxValue: z.number().optional(),
    actionLabel: z.string().optional(),
    actionUrl: z.string().url().optional(),
  })
  .refine(
    (data) => {
      // Validate SingleChoice and MultipleChoice have at least 2 options
      if (data.type === 'SingleChoice' || data.type === 'MultipleChoice') {
        return data.options && data.options.length >= 2
      }
      return true
    },
    {
      message:
        'Single choice and multiple choice questions must have at least 2 options',
      path: ['options'],
    }
  )
  .refine(
    (data) => {
      // Validate Rating has min and max values
      if (data.type === 'Rating') {
        return data.minValue !== undefined && data.maxValue !== undefined
      }
      return true
    },
    {
      message: 'Rating questions must have minimum and maximum values',
      path: ['minValue'],
    }
  )
  .refine(
    (data) => {
      // Validate Rating min < max
      if (
        data.type === 'Rating' &&
        data.minValue !== undefined &&
        data.maxValue !== undefined
      ) {
        return data.minValue < data.maxValue
      }
      return true
    },
    {
      message: 'Maximum value must be greater than minimum value',
      path: ['maxValue'],
    }
  )
  .refine(
    (data) => {
      // Validate CTA has action label and URL
      if (data.type === 'CTA') {
        return (
          data.actionLabel &&
          data.actionLabel.trim() &&
          data.actionUrl &&
          data.actionUrl.trim()
        )
      }
      return true
    },
    {
      message: 'CTA questions must have button text and action URL',
      path: ['actionLabel'],
    }
  )

// Main survey form schema
export const fnsFormSchema = z.object({
  title: z
    .string()
    .min(1, 'Survey title is required')
    .max(200, 'Title must be less than 200 characters'),
  description: z.string().optional(),
  type: SurveyTypeEnum,
  isActive: z.boolean(),
  isAnonymous: z.boolean(),
  questions: z
    .array(fnsQuestionSchema)
    .min(1, 'At least one question is required'),
})

export type FnsFormData = z.infer<typeof fnsFormSchema>

// Helper function to validate individual question
export const validateFnsQuestion = (question: unknown) => {
  return fnsQuestionSchema.safeParse(question)
}
